import { withAnalytics } from '@veneer/analytics';
import { IconInfo } from '@veneer/core';
import React, { useMemo, useRef } from 'react';
import Tooltip from '../tooltip';
import { ListItemProps } from './types';
import Badge from '../badge';
import { generateID } from '../../base/helper';
import './styles.scss';

export type ListItemProp = {
  item: ListItemProps;
  index: number;
  selectedIndex: number;
  onClick: (index: number) => void;
  onMouseEnter: (index: number) => void;
  onMouseLeave: (index: number) => void;
  onHover: (index: number) => void;
  dividerColor?: string;
  id?: string;
};
const TListItem: React.FC<ListItemProp> = ({
  item,
  index,
  selectedIndex,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onHover,
  dividerColor,
  id = generateID('clientos-ui-toolkit-list-container')
}) => {
  const listItemRef = useRef<HTMLDivElement>(null);
  const clsBase = 'clientos-ui-toolkit-list-container__item';
  const clsDividerBase = 'clientos-ui-toolkit-list-container__divider';

  const isReactElement = React.isValidElement(item.end);
  const isDisabled =
    isReactElement && (item.end as React.ReactElement).props?.disabled === true;
  const isFirst = index === 0;

  const clsItem = useMemo(() => {
    let cls = clsBase;
    if (item.paddingStart !== false) cls += ' padding-start';
    if (item.paddingEnd !== false) cls += ' padding-end';
    if (index === selectedIndex) cls += ' selected';
    if (item.onClick || item.onMouseEnter || item.onHover) cls += ' hoverable';
    if (item.disabled) cls += ' disabled';
    if (item.error) cls += ' error';

    return cls;
  }, [
    item.paddingStart,
    item.paddingEnd,
    item.onClick,
    item.onMouseEnter,
    item.onHover,
    item.disabled,
    item.error,
    index,
    selectedIndex
  ]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === 'enter' || e.key.toLowerCase() === ' ') {
      onClick(index);
      blurItem();
    }
  };

  const blurItem = () => {
    listItemRef.current?.blur();
  };

  const hasTooltip = !!item.tooltip;
  const hasBadge = !!item.labelBadge;
  const hasInteractiveEnd =
    React.isValidElement(item.end) &&
    typeof item.end.props?.onClick === 'function' &&
    !item.disabled;
  const isInteractive =
    (!!item.onClick || !!item.onHover || !!item.onMouseEnter) && !item.disabled;
  const isAccessible =
    isInteractive || hasTooltip || hasBadge || hasInteractiveEnd;

  return (
    <>
      {!isFirst && (
        <div
          key={`divider-${index}`}
          className={clsDividerBase}
          style={{
            borderBottomColor: `${dividerColor}`
          }}
          role="separator"
          aria-hidden="true"
        />
      )}
      <div
        id={id}
        className={clsItem}
        onClick={
          isInteractive
            ? () => {
                onClick(index);
                blurItem();
              }
            : undefined
        }
        onKeyPress={isInteractive ? handleKeyPress : undefined}
        onMouseEnter={() => {
          onMouseEnter(index);
          onHover(index);
          blurItem();
        }}
        onFocus={() => {
          onMouseEnter(index);
          onHover(index);
        }}
        onMouseLeave={() => {
          onMouseLeave(index);
          blurItem();
        }}
        role={isAccessible ? 'button' : undefined}
        aria-labelledby={isAccessible ? `${id}__label-text` : undefined}
        ref={listItemRef}
        aria-selected={isAccessible ? index === selectedIndex : undefined}
        aria-disabled={
          isAccessible ? (item.disabled ? 'true' : 'false') : undefined
        }
        tabIndex={isInteractive ? 0 : -1}
      >
        <div
          className="clientos-ui-toolkit-list-container__header"
          id={`${id}__header`}
          tabIndex={-1}
        >
          {item.start && (
            <div
              className={`clientos-ui-toolkit-list-container__start-icon align-${item.alignStart ?? 'top'}`}
              id={`${id}__start`}
            >
              {item.start}
            </div>
          )}
          {(item.label || item.end || item.tooltip || item.description) && (
            <div
              className="clientos-ui-toolkit-list-container__start-content"
              id={`${id}__start-content`}
              tabIndex={-1}
            >
              <div
                className="clientos-ui-toolkit-list-container__start-center-content"
                id={`${id}__start-center-content`}
              >
                <div
                  className="clientos-ui-toolkit-list-container__label body-large"
                  id={`${id}__label`}
                >
                  <span id={`${id}__label-text`}>{item.label}</span>
                  {(item.tooltip || item.labelBadge) && (
                    <div
                      className={`clientos-ui-toolkit-list-container__info-container ${item.tooltip ? '' : 'without-tooltip'}`}
                      id={`${id}__info-container`}
                    >
                      {item.tooltip && (
                        <Tooltip
                          content={item.tooltip}
                          id={`${id}__tooltip`}
                        >
                          <IconInfo />
                        </Tooltip>
                      )}
                      {item.labelBadge && (
                        <Badge
                          id={`${id}__label-badge`}
                          variant={item.labelBadge}
                          badgeType={`DOT`}
                        />
                      )}
                    </div>
                  )}
                </div>
                {((item.description && item.description !== '') ||
                  item.descriptionBadge) && (
                  <div
                    className="clientos-ui-toolkit-list-container__description-container"
                    id={`${id}__description-container`}
                  >
                    {item.descriptionBadge && (
                      <Badge
                        id={`${id}__description-badge`}
                        variant={item.descriptionBadge}
                        badgeType="DOT"
                      />
                    )}
                    {item.description && (
                      <div
                        className="clientos-ui-toolkit-list-container__description body-regular"
                        id={`${id}__description`}
                      >
                        {item.description}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {item.end && (
                <div
                  className={`clientos-ui-toolkit-list-container__end-content align-${item.alignEnd ?? 'top'}`}
                  id={`${id}__end`}
                >
                  {isReactElement
                    ? React.cloneElement(item.end as React.ReactElement<any>, {
                        disabled: item.disabled || isDisabled
                      })
                    : item.end}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className={`clientos-ui-toolkit-list-container__body ${item.start ? 'with-icon' : ''}`}
          id={`${id}__body`}
        >
          {(item.secondaryControl ||
            (item.secondaryText && item.secondaryText !== '')) && (
            <div id={`${id}__body-content`}>
              {item.secondaryControl && (
                <div
                  className={`clientos-ui-toolkit-list-container__body__secondary-control align-${item.alignStart ?? 'top'}`}
                  id={`${id}__secondary-control`}
                >
                  {item.secondaryControl}
                </div>
              )}
              {item.secondaryText && (
                <div id={`${id}__body-text`}>
                  <div
                    className="clientos-ui-toolkit-list-container__body__secondary-text body-regular"
                    id={`${id}__secondary-text`}
                  >
                    {item.secondaryText}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const ListItemAnalytics = withAnalytics(TListItem);
export default TListItem;
